import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, CircularProgress, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerIcon from '@mui/icons-material/Power';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import api from '../utils/apiInterceptor'; // 🔥【QA问题3修复】原经 src/api/api.js 转发，现直接使用统一封装实例

// 状态中文与配色
const STATUS_MAP = {
  idle: { label: '空闲', color: '#00ff9f' },
  busy: { label: '占用中', color: '#ffd700' },
  muted: { label: '禁言中', color: '#ff6b6b' },
  cooldown: { label: '冷却中', color: '#ff8a80' },
  dailyLimit: { label: '今日额度用尽', color: '#ffa726' },
  invalid: { label: '已失效', color: '#9e9e9e' },
  disabled: { label: '已停用', color: '#757575' }
};

// 错误码中文化（lastError 存英文 kind/网络错误，界面转成可读说明）
const errCn = (e) => {
  if (!e) return '';
  const map = {
    muted: '触发DeepSeek风控限流（发送过频/单日总量超限，禁言到期自动恢复）',
    empty: '返回空内容（触发风控拦截或模型无响应）',
    invalid: 'token失效（401/403鉴权拒绝）',
    network: '网络错误/请求超时',
    cooldown: '连续失败进入冷却',
    dailyLimit: '当日调用量达到预算线'
  };
  return map[e] || e;
};

// 状态说明（悬浮提示）：禁言原因/恢复时间、失效原因、停用说明等
const statusTip = (a) => {
  const fmtT = (t) => (t ? new Date(t).toLocaleString('zh-CN', { hour12: false }) : '');
  switch (a.status) {
    case 'muted': return `禁言至 ${fmtT(a.muteUntil)} · 触发原因: ${errCn(a.stats?.lastError) || errCn('muted')}`;
    case 'cooldown': return `连续失败冷却至 ${fmtT(a.cooldownUntil)}${a.stats?.lastError ? ` · 最近错误: ${errCn(a.stats.lastError)}` : ''}`;
    case 'dailyLimit': return '当日调用量达到预算线，次日零点自动恢复';
    case 'invalid': return `token 已失效（${errCn(a.stats?.lastError) || '401/403鉴权拒绝'}），需重新续期或删除`;
    case 'disabled': return '手动停用（号池管理器中可重新启用；停用原因见备注或最近错误）';
    case 'busy': return `占用中（${a.occupiedBy || '未知任务'}）`;
    default: return '';
  }
};

const StatCard = ({ title, value, color = '#00ff9f', suffix = '', sub = '' }) => (
  <Card sx={{ flex: 1, minWidth: 118, bgcolor: 'rgba(6, 21, 44, 0.85)', border: '1px solid rgba(0, 255, 159, 0.15)' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.5)', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Typography>
      {/* 亿级调用量：字号收敛16px+缩写单位，保证完整显示不溢出 */}
      <Typography sx={{ color, fontWeight: 'bold', mt: 0.5, fontSize: 16, whiteSpace: 'nowrap' }}>
        {value}<Typography component="span" variant="caption" sx={{ color: 'rgba(214,227,252,0.5)', ml: 0.5 }}>{suffix}</Typography>
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.35)', display: 'block', mt: 0.25 }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

// 大数字缩写：亿级→X.XX亿，万级→X.XX万
const fmtBig = (n) => {
  n = n || 0;
  return n >= 1e8 ? (n / 1e8).toFixed(2) + '亿' : n >= 1e4 ? (n / 1e4).toFixed(2) + '万' : String(n);
};

const ReversePoolManagement = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [reserveToggling, setReserveToggling] = useState(false);
  const [daily, setDaily] = useState(null);       // 按日调用明细（今日 + 近7天）
  const [dailyDate, setDailyDate] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/kOjtx3AXNmCsT5Wz/reverse/status');
      if (res.data?.success) setStatus(res.data);
    } catch (e) {
      console.error('获取逆向状态失败:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // 按日调用明细：默认查当月，页面展示"今日明细 + 近7天汇总"
  const loadDaily = useCallback(async () => {
    try {
      const month = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Shanghai' }).slice(0, 7);
      const res = await api.get(`/kOjtx3AXNmCsT5Wz/reverse/daily-stats?month=${month}`);
      if (res.data?.success) {
        setDaily(res.data);
        setDailyDate(new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Shanghai' }));
      }
    } catch (e) {
      console.error('获取调用明细失败:', e);
    }
  }, []);

  useEffect(() => { loadStatus(); loadDaily(); }, [loadStatus, loadDaily]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await api.post('/kOjtx3AXNmCsT5Wz/reverse/toggle', { enabled: !status.enabled });
      await loadStatus();
    } catch (e) {
      console.error('切换开关失败:', e);
    } finally {
      setToggling(false);
    }
  };

  // 🔴本地预留开关：开=50个本地测试号独占给本地调试（云端禁用）；关=测试号让给云端调度，不空转浪费
  const handleReserveToggle = async () => {
    setReserveToggling(true);
    try {
      await api.post('/kOjtx3AXNmCsT5Wz/reverse/local-reserve', { enabled: !(status.config?.localReserve !== false) });
      await loadStatus();
    } catch (e) {
      console.error('切换本地预留开关失败:', e);
    } finally {
      setReserveToggling(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#00ff9f' }} /></Box>;
  }
  if (!status) {
    return <Typography sx={{ color: '#ff6b6b', py: 4 }}>加载失败，请刷新重试</Typography>;
  }

  const myOwner = status.myOwner || 'cloud';
  // 🔴预留开关状态：必须在 status 守卫之后求值（status 为 null 时此处不可达，避免 config 读取崩溃）
  const reserveOn = status.config?.localReserve !== false;
  const OWNER_MAP = { cloud: { label: '云端生产', color: '#4fc3f7' }, local: { label: '本地测试', color: '#ffb74d' } };
  const stats = status.config?.stats || {};
  const accounts = status.accounts || [];
  // 分组统计：云端生产组与本地测试组各自独立汇总（云端调用量大，单独核算）
  const groupAccountsBy = (owner) => accounts.filter(a => (a.owner === 'local' ? 'local' : 'cloud') === owner);
  const computeGroupStats = (list) => {
    const g = { idle: 0, busy: 0, muted: 0, calls: 0, ok: 0, out: 0 };
    list.forEach(a => {
      g[a.status] = (g[a.status] || 0) + 1;
      g.calls += a.stats?.totalCalls || 0;
      g.ok += a.stats?.successCalls || 0;
      g.out += a.stats?.totalOutputChars || 0;
    });
    g.rate = g.calls > 0 ? ((g.ok / g.calls) * 100).toFixed(1) + '%' : '-';
    return g;
  };
  const cloudAccounts = groupAccountsBy('cloud');
  const localAccounts = groupAccountsBy('local');
  const cloudStats = computeGroupStats(cloudAccounts);
  const localStats = computeGroupStats(localAccounts);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* 总开关区 */}
      <Card sx={{ mb: 2, bgcolor: 'rgba(6, 21, 44, 0.9)', border: `1px solid ${status.enabled ? 'rgba(0,255,159,0.4)' : 'rgba(255,107,107,0.25)'}` }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <PowerIcon sx={{ color: status.enabled ? '#00ff9f' : '#757575' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#e4f1ff', fontWeight: 'bold' }}>
              DeepSeek 逆向通道 {status.enabled ? '（已开启）' : '（已关闭）'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.5)' }}>
              开启后：章节生成/重写/优化/后置提取优先走逆向号池，失败自动降级官方API；关闭后全部走官方API
            </Typography>
          </Box>
          <Tooltip title={status.enabled ? '点击关闭逆向通道' : '点击开启逆向通道'}>
            <Switch
              checked={status.enabled}
              onChange={handleToggle}
              disabled={toggling}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#00ff9f' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(0,255,159,0.4)' }
              }}
            />
          </Tooltip>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadStatus}
            sx={{ color: '#00ff9f', borderColor: 'rgba(0,255,159,0.4)', '&:hover': { borderColor: '#00ff9f' } }}
            variant="outlined">刷新</Button>
        </CardContent>
      </Card>

      {/* 🔴本地预留开关：50个本地测试号是独占给本地调试，还是让给云端生产调度（避免闲置浪费） */}
      <Card sx={{ mb: 2, bgcolor: 'rgba(6, 21, 44, 0.9)', border: `1px solid ${reserveOn ? 'rgba(255,183,77,0.4)' : 'rgba(79,195,247,0.4)'}` }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <DeveloperBoardIcon sx={{ color: reserveOn ? '#ffb74d' : '#4fc3f7' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#e4f1ff', fontWeight: 'bold' }}>
              本地测试号预留 {reserveOn ? '（预留中）' : '（已让给云端）'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.5)' }}>
              {reserveOn
                ? `${localAccounts.length} 个本地测试号仅供本地调试使用，云端禁用调度；云端可调度 ${cloudAccounts.length} 个生产号。不调试时可关闭，把测试号还给云端。`
                : `测试号已让给云端：云端可调度全部 ${cloudAccounts.length + localAccounts.length} 个号（含 ${localAccounts.length} 个测试号）；本地端将暂停用号，30秒内自动同步生效。`}
            </Typography>
          </Box>
          <Tooltip title={reserveOn ? '点击关闭预留：测试号让给云端生产调度' : '点击开启预留：测试号独占给本地调试'}>
            <Switch
              checked={reserveOn}
              onChange={handleReserveToggle}
              disabled={reserveToggling}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffb74d' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(255,183,77,0.4)' }
              }}
            />
          </Tooltip>
        </CardContent>
      </Card>

      {/* 池状态卡片：云端生产组与本地测试组分开统计（非本端组为部署快照，需明确标注） */}
      <Typography variant="subtitle2" sx={{ color: OWNER_MAP.cloud.color, fontWeight: 'bold', mb: 1 }}>
        云端生产组 · {cloudAccounts.length} 个账号
        {myOwner === 'cloud' ? '（本端调度中 · 实时数据）' : `（快照数据 · ${cloudAccounts.length > 0 ? '该组由云端服务器调度，实时数据在云端号池中' : ''}）`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StatCard title="可用账号" value={`${cloudStats.idle || 0} / ${cloudAccounts.length}`} />
        <StatCard title="占用中" value={cloudStats.busy || 0} color="#ffd700" />
        <StatCard title="禁言中" value={cloudStats.muted || 0} color="#ff6b6b" />
        <StatCard title="累计调用" value={fmtBig(cloudStats.calls)} />
        <StatCard title="成功率" value={cloudStats.rate} />
        <StatCard title="累计输出" value={fmtBig(cloudStats.out)} suffix="字" />
        <StatCard title="降级官方" value={stats.degradeToOfficialCount || 0} color="#ff8a80" suffix="次" />
      </Box>
      <Typography variant="subtitle2" sx={{ color: OWNER_MAP.local.color, fontWeight: 'bold', mb: 1 }}>
        本地测试组 · {localAccounts.length} 个账号
        {myOwner === 'local' ? '（本端调度中 · 实时数据）' : `（快照数据 · ${localAccounts.length > 0 ? '该组由本地端调度，实时数据在本地号池中' : ''}）`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StatCard title="可用账号" value={`${localStats.idle || 0} / ${localAccounts.length}`} />
        <StatCard title="占用中" value={localStats.busy || 0} color="#ffd700" />
        <StatCard title="禁言中" value={localStats.muted || 0} color="#ff6b6b" />
        <StatCard title="累计调用" value={fmtBig(localStats.calls)} />
        <StatCard title="累计输出" value={fmtBig(localStats.out)} suffix="字" />
      </Box>

      {/* 账号简表（详细管理请用号池管理器桌面软件） */}
      <Card sx={{ bgcolor: 'rgba(6, 21, 44, 0.9)', border: '1px solid rgba(0,255,159,0.15)' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ color: '#e4f1ff', fontWeight: 'bold', mb: 1.5 }}>
            账号池概览
            <Typography component="span" variant="caption" sx={{ color: 'rgba(214,227,252,0.4)', ml: 1.5 }}>
              增删账号/限流档案/事件日志请使用「号池管理器」桌面软件
            </Typography>
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { color: 'rgba(214,227,252,0.6)', borderColor: 'rgba(0,255,159,0.15)', fontSize: '0.78rem' } }}>
                  <TableCell>账号</TableCell>
                  <TableCell>归属</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell align="right">调用次数</TableCell>
                  <TableCell align="right">成功/失败</TableCell>
                  <TableCell align="right">累计输出</TableCell>
                  <TableCell>最近使用</TableCell>
                  <TableCell>最近错误</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((a) => {
                  const st = STATUS_MAP[a.status] || { label: a.status, color: '#9e9e9e' };
                  const ow = OWNER_MAP[(a.owner === 'local') ? 'local' : 'cloud'];
                  return (
                    <TableRow key={a.id} sx={{ '& td': { color: 'rgba(214,227,252,0.85)', borderColor: 'rgba(0,255,159,0.08)', fontSize: '0.8rem' } }}>
                      <TableCell>{a.remark || a.id}</TableCell>
                      <TableCell>
                        <Chip label={ow.label} size="small"
                          sx={{ color: ow.color, borderColor: ow.color, bgcolor: 'transparent', border: 1, height: 22 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" title={statusTip(a)}
                          sx={{ color: st.color, borderColor: st.color, bgcolor: 'transparent', border: 1, height: 22, cursor: statusTip(a) ? 'help' : 'default' }} />
                      </TableCell>
                      <TableCell align="right">{a.stats?.totalCalls || 0}</TableCell>
                      <TableCell align="right">
                        <span style={{ color: '#00ff9f' }}>{a.stats?.successCalls || 0}</span>
                        <span style={{ color: 'rgba(214,227,252,0.4)' }}> / </span>
                        <span style={{ color: (a.stats?.failedCalls || 0) > 0 ? '#ff6b6b' : 'rgba(214,227,252,0.4)' }}>{a.stats?.failedCalls || 0}</span>
                      </TableCell>
                      <TableCell align="right">{fmtBig(a.stats?.totalOutputChars || 0)}字</TableCell>
                      <TableCell>{a.stats?.lastUsedAt ? new Date(a.stats.lastUsedAt).toLocaleString('zh-CN', { hour12: false }) : '未使用'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={errCn(a.stats?.lastError)}>
                        {errCn(a.stats?.lastError) || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 调用明细：今日账号级明细 + 近7天汇总（数据源 /reverse/daily-stats，按北京时间切天） */}
      <Card sx={{ mb: 2, bgcolor: 'rgba(6, 21, 44, 0.9)', border: '1px solid rgba(0,255,159,0.15)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#e4f1ff', fontWeight: 'bold', mb: 0.5 }}>调用明细</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.5)', display: 'block', mb: 2 }}>
            按天 × 按账号统计 · 日期按北京时间切天 · 本地记录与云端记录分开核算 · 完整月度明细请用号池管理器桌面软件
          </Typography>
          {(() => {
            const days = daily?.days || {};
            const today = days[dailyDate] || { summary: {}, byOwner: {}, accounts: {} };
            const sm = today.summary || {};
            const bo = today.byOwner || {};
            const rows = Object.entries(today.accounts || {})
              .map(([id, a]) => ({ id, ...a }))
              .sort((x, y) => (y.calls || 0) - (x.calls || 0) || (y.outputChars || 0) - (x.outputChars || 0));
            const accMap = new Map(accounts.map(a => [a.id, a]));
            const last7 = Object.keys(days).sort().slice(-7).reverse();
            const groupCards = (key, label, color, note) => {
              const g = bo[key] || {};
              return (
                <>
                  <Typography variant="subtitle2" sx={{ color, fontWeight: 'bold', mt: 1.5, mb: 1 }}>
                    {label} · 今日调用
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(214,227,252,0.4)', ml: 1 }}>{note}</Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <StatCard title="调用次数" value={(g.calls || 0).toLocaleString()} />
                    <StatCard title="成功/失败" value={`${(g.success || 0).toLocaleString()} / ${(g.fail || 0).toLocaleString()}`} />
                    <StatCard title="使用账号数" value={g.accountsUsed || 0} suffix="个" />
                    <StatCard title="输出字数" value={fmtBig(g.outputChars)} suffix="字" />
                    <StatCard title="禁言次数" value={g.muted || 0} color="#ff6b6b" suffix="次" />
                  </Box>
                </>
              );
            };
            return (
              <>
                {groupCards('cloud', '云端生产组', OWNER_MAP.cloud.color, '由云服务器调度产生 · 生产数据')}
                {groupCards('local', '本地测试组', OWNER_MAP.local.color, '由本地调试调度产生 · 测试数据')}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <StatCard title={`全部合计（${dailyDate.slice(5) || ''}）`} value={fmtBig(sm.calls)} />
                  <StatCard title="发送字数" value={fmtBig(sm.promptChars)} suffix="字" />
                  <StatCard title="思考字数" value={fmtBig(sm.thinkingChars)} suffix="字" />
                  <StatCard title="降级官方" value={sm.degrade || 0} color="#ff8a80" suffix="次" />
                </Box>
                <TableContainer sx={{ maxHeight: 360, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>账号</TableCell>
                        <TableCell>归属</TableCell>
                        <TableCell align="right">调用</TableCell>
                        <TableCell align="right">成功/失败</TableCell>
                        <TableCell align="right">发送字数</TableCell>
                        <TableCell align="right">输出字数</TableCell>
                        <TableCell align="right">禁言</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((a) => {
                        const ow = OWNER_MAP[(a.owner === 'local') ? 'local' : 'cloud'];
                        const info = accMap.get(a.id);
                        return (
                          <TableRow key={a.id} sx={{ '& td': { color: 'rgba(214,227,252,0.85)', borderColor: 'rgba(0,255,159,0.08)', fontSize: '0.8rem' } }}>
                            <TableCell>{info?.remark || a.remark || a.id}</TableCell>
                            <TableCell>
                              <Chip label={ow.label} size="small"
                                sx={{ color: ow.color, borderColor: ow.color, bgcolor: 'transparent', border: 1, height: 22 }} />
                            </TableCell>
                            <TableCell align="right">{(a.calls || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <span style={{ color: '#00ff9f' }}>{(a.success || 0).toLocaleString()}</span>
                              <span style={{ color: 'rgba(214,227,252,0.4)' }}> / </span>
                              <span style={{ color: (a.fail || 0) > 0 ? '#ff6b6b' : 'rgba(214,227,252,0.4)' }}>{(a.fail || 0).toLocaleString()}</span>
                            </TableCell>
                            <TableCell align="right">{fmtBig(a.promptChars)}</TableCell>
                            <TableCell align="right">{fmtBig(a.outputChars)}</TableCell>
                            <TableCell align="right" sx={{ color: (a.muted || 0) > 0 ? '#ff6b6b' : undefined }}>{a.muted || 0}</TableCell>
                          </TableRow>
                        );
                      })}
                      {rows.length === 0 && (
                        <TableRow><TableCell colSpan={7} sx={{ color: 'rgba(214,227,252,0.4)' }}>今日暂无调用记录</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {last7.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(214,227,252,0.7)', mb: 1 }}>近7天汇总（云端/本地分列）</Typography>
                    <TableContainer sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>日期</TableCell>
                            <TableCell align="right">云端调用</TableCell>
                            <TableCell align="right">本地调用</TableCell>
                            <TableCell align="right">调用合计</TableCell>
                            <TableCell align="right">使用用户数</TableCell>
                            <TableCell align="right">成功/失败</TableCell>
                            <TableCell align="right">使用账号数</TableCell>
                            <TableCell align="right">输出字数</TableCell>
                            <TableCell align="right">禁言</TableCell>
                            <TableCell align="right">降级</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {last7.map((d) => {
                            const day = days[d] || {};
                            const s = day.summary || {};
                            const bc = (day.byOwner?.cloud?.calls) || 0;
                            const bl = (day.byOwner?.local?.calls) || 0;
                            return (
                              <TableRow key={d} sx={{ '& td': { color: 'rgba(214,227,252,0.85)', borderColor: 'rgba(0,255,159,0.08)', fontSize: '0.8rem' } }}>
                                <TableCell>{d}{d === dailyDate ? '（今天）' : ''}</TableCell>
                                <TableCell align="right" sx={{ color: OWNER_MAP.cloud.color }}>{bc.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ color: OWNER_MAP.local.color }}>{bl.toLocaleString()}</TableCell>
                                <TableCell align="right"><b>{(s.calls || 0).toLocaleString()}</b></TableCell>
                                <TableCell align="right" sx={{ color: '#4fc3f7' }}>{s.userCount || 0}</TableCell>
                                <TableCell align="right">{(s.success || 0).toLocaleString()} / {(s.fail || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{s.accountsUsed || 0}</TableCell>
                                <TableCell align="right">{fmtBig(s.outputChars)}</TableCell>
                                <TableCell align="right">{s.muted || 0}</TableCell>
                                <TableCell align="right">{s.degrade || 0}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* 用户调用明细（独立统计）：当天多少用户用过、每个用户调了多少号、生成多少字 */}
      <Card sx={{ bgcolor: 'rgba(6, 21, 44, 0.9)', border: '1px solid rgba(79,195,247,0.25)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#4fc3f7', fontWeight: 'bold', mb: 0.5 }}>用户调用明细（{dailyDate}）</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.5)', display: 'block', mb: 2 }}>
            按发起生成的平台用户统计 · 记录每个用户当天用了多少个号、生成多少字 · 系统内部调用（无用户上下文）不计入
          </Typography>
          {(() => {
            const days = daily?.days || {};
            const today = days[dailyDate] || { users: {} };
            const users = Object.entries(today.users || {})
              .map(([id, u]) => ({ id, ...u }))
              .sort((x, y) => (y.calls || 0) - (x.calls || 0) || (y.outputChars || 0) - (x.outputChars || 0));
            const accMap = new Map(accounts.map(a => [a.id, a]));
            const totalCalls = users.reduce((s, u) => s + (u.calls || 0), 0);
            const totalOut = users.reduce((s, u) => s + (u.outputChars || 0), 0);
            return (
              <>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <StatCard title="今日使用用户数" value={users.length} color="#4fc3f7" suffix="人" />
                  <StatCard title="用户调用合计" value={totalCalls.toLocaleString()} />
                  <StatCard title="用户生成字数" value={fmtBig(totalOut)} suffix="字" />
                </Box>
                <TableContainer sx={{ maxHeight: 360, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>用户</TableCell>
                        <TableCell align="right">调用次数</TableCell>
                        <TableCell align="right">成功/失败</TableCell>
                        <TableCell align="right">生成字数</TableCell>
                        <TableCell align="right">用号数</TableCell>
                        <TableCell align="right">禁言/降级</TableCell>
                        <TableCell>使用账号明细</TableCell>
                        <TableCell>最近调用</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((u) => {
                        const accEntries = Object.entries(u.accounts || {}).sort((x, y) => y[1] - x[1]);
                        return (
                          <TableRow key={u.id} sx={{ '& td': { color: 'rgba(214,227,252,0.85)', borderColor: 'rgba(79,195,247,0.08)', fontSize: '0.8rem' } }}>
                            <TableCell sx={{ color: '#e4f1ff', fontWeight: 'bold' }}>{u.username || u.id}</TableCell>
                            <TableCell align="right">{(u.calls || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <span style={{ color: '#00ff9f' }}>{(u.success || 0).toLocaleString()}</span>
                              <span style={{ color: 'rgba(214,227,252,0.4)' }}> / </span>
                              <span style={{ color: (u.fail || 0) > 0 ? '#ff6b6b' : 'rgba(214,227,252,0.4)' }}>{(u.fail || 0).toLocaleString()}</span>
                            </TableCell>
                            <TableCell align="right">{fmtBig(u.outputChars)}字</TableCell>
                            <TableCell align="right">{accEntries.length}</TableCell>
                            <TableCell align="right">
                              <span style={{ color: (u.muted || 0) > 0 ? '#ff6b6b' : 'rgba(214,227,252,0.4)' }}>{u.muted || 0}</span>
                              <span style={{ color: 'rgba(214,227,252,0.4)' }}> / </span>
                              <span style={{ color: (u.degrade || 0) > 0 ? '#ff8a80' : 'rgba(214,227,252,0.4)' }}>{u.degrade || 0}</span>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              {accEntries.map(([aid, c]) => (
                                <Chip key={aid} size="small" label={`${accMap.get(aid)?.remark || aid} ×${c}`}
                                  title={accMap.get(aid)?.remark || aid}
                                  sx={{ color: 'rgba(214,227,252,0.75)', borderColor: 'rgba(79,195,247,0.3)', bgcolor: 'transparent', border: 1, height: 20, mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                              ))}
                              {accEntries.length === 0 && '-'}
                            </TableCell>
                            <TableCell>{u.lastAt ? new Date(u.lastAt).toLocaleString('zh-CN', { hour12: false }) : '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {users.length === 0 && (
                        <TableRow><TableCell colSpan={8} sx={{ color: 'rgba(214,227,252,0.4)' }}>今日暂无用户调用记录</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReversePoolManagement;
